import { useCheckoutPermissions, useReservationPermissions, useUserPermissions } from '@cheqroom/hooks';
import type { TFunction } from 'i18next';
import { FC, ReactElement } from 'react';
import { Trans } from 'react-i18next';

import { Icon } from '../../../Icon';
import { Info } from '../../../Info';
import { useInfoT } from '../../../Info/Info.context';
import { useKitInfoContext } from '../../KitInfo.context';
import { getKitStatusColor, getKitStatusIcon, getKitStatusText, isKitUnavailable } from './utils';

type Props = {
	renderCustodyLink: (userId: string) => ReactElement;
};

export const StatusContent: FC<Props> = ({ renderCustodyLink }) => {
	const { kit, user, workspace } = useKitInfoContext();
	const t = useInfoT('kit-info');
	const permissionProps = { user: user ?? null, workspace: workspace ?? null };
	const canReadReservations = !!useReservationPermissions(permissionProps).canRead(true);
	const canReadCheckouts = !!useCheckoutPermissions(permissionProps).canRead(true);
	const canSeeUsers = useUserPermissions(permissionProps).canSeeUsers();

	if (kit.status === 'empty') return null;

	const isUnavailable = isKitUnavailable(kit, canReadCheckouts, canReadReservations);
	const statusText = getKitStatusText(kit, { isUnavailable, canSeeUsers });
	const i18nKey = `status.${statusText.key}`;

	const text =
		statusText.key === 'in_custody_with_link' ? (
			<Trans
				t={t as unknown as TFunction}
				i18nKey={i18nKey}
				values={{ inCustodyOf: statusText.values.inCustodyOf }}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
				components={[renderCustodyLink(statusText.values.userId)] as any}
			/>
		) : (
			t(i18nKey)
		);

	return (
		<Info
			icon={
				<Icon
					source={getKitStatusIcon(kit.status)}
					size="small"
					className={getKitStatusColor(kit.status, isUnavailable)}
				/>
			}
			text={text}
		/>
	);
};
